import { Autocomplete, Button, SlideOut } from '@sd/prism';
import { field, parsers, requestRunsHelper, useForm, useQueryParam } from '@sd/shared';
import { clone } from 'ramda';
import { ChangeEvent, SetStateAction, useState } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { Select } from '../Select/Select';

type Props = {
  isOpen: boolean;
  onChangeDialogOpen: { (value: SetStateAction<boolean>): void; (arg0: boolean): void };
};

export const RequestRuns = ({ isOpen, onChangeDialogOpen }: Props) => {
  const accountData = requestRunsHelper.mockAccountData();
  const auctionData = requestRunsHelper.mockAuctionData();
  const dateData = requestRunsHelper.mockDateData();
  const preferredLanesData = requestRunsHelper.mockPreferredData();

  const [numberOfRuns] = useQueryParam('numberOfRuns', { parser: parsers.number() });
  const [preferredLane] = useQueryParam('preferredLane', { parser: parsers.number() });
  const [comment, setComment] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const form = useForm({
    numberOfRuns: field.config<number>({
      initialValue: numberOfRuns
    }),
    preferredLane: field.config<number>({
      initialValue: preferredLane
    })
  });

  const [numberOfRunsAndPreferredLane, setNumberOfRunsAndPreferredLane] = useState<any[]>([form]);

  const addNewNumberOfRunsAndPreferredLane = () => {
    setNumberOfRunsAndPreferredLane([...clone(numberOfRunsAndPreferredLane), clone(form)]);
  };

  const removeNewNumberOfRunsAndPreferredLane = (indexToRemove: number) => {
    setNumberOfRunsAndPreferredLane(numberOfRunsAndPreferredLane.filter((_, index) => index !== indexToRemove));
  };

  const handleNumberOfRunChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const numberOfForm = clone(numberOfRunsAndPreferredLane);
    // eslint-disable-next-line functional/immutable-data
    numberOfForm[index].fields.numberOfRuns.controlValue = +e.target.value;
    setNumberOfRunsAndPreferredLane(numberOfForm);
  };

  return (
    <RequestRunsSlideOut data-testid="req-run-slide-out">
      <SlideOut actionable onCloseButtonClick={() => onChangeDialogOpen(false)} open={isOpen} placement="right">
        <SlideOutHeader data-testid="slide-out-header">Request Runs</SlideOutHeader>
        <SlideOutBody>
          <ItemWrapper>
            <label>Select Auction</label>
            <AutocompleteCustom className="mb-autocomplete-padding">
              <Autocomplete
                placeholder="Input keyword here..."
                className="autoCustomInput"
                options={auctionData}
                minLength={0}
              />
            </AutocompleteCustom>
          </ItemWrapper>
          <ItemWrapper>
            <label>Date</label>
            <DropdownCustom>
              <Select
                options={dateData}
                value="08/14/2022"
                onChange={dateValue => setSelectedDate(dateValue)}
                disabled
              />
            </DropdownCustom>
          </ItemWrapper>
          <ItemWrapper>
            <label>Select Account</label>
            <AutocompleteCustom className="mb-autocomplete-padding">
              <Autocomplete
                placeholder="Input keyword here..."
                className="autoCustomInput"
                options={accountData}
                minLength={0}
              />
            </AutocompleteCustom>
          </ItemWrapper>
          {numberOfRunsAndPreferredLane.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ItemFormGroup key={index}>
              <Form.Group className="col-4 unset-padding unset-margin mb-number-padding">
                <Form.Label htmlFor="numberOfRuns">Number of Runs</Form.Label>
                <Form.Control
                  data-testid={`number-runs-input-${index}`}
                  className="number-runs-input"
                  id={index.toLocaleString()}
                  value={item.fields.numberOfRuns.controlValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleNumberOfRunChange(e, index)}
                />
              </Form.Group>

              <Form.Group className="col-6 unset-margin mb-prefer-padding">
                <Form.Label htmlFor="preferredLane">Preferred Lane (Optional) </Form.Label>
                <Select
                  options={preferredLanesData}
                  value={selectedDate}
                  emptyOption={{ label: 'No Preferred Lane', value: undefined }}
                  disabled
                />
              </Form.Group>
              {index === 0 ? (
                <div className="col-1 unset-padding btn-group-container">
                  <Button onClick={addNewNumberOfRunsAndPreferredLane} className="btn-plus" data-testid="btn-plus">
                    +
                  </Button>
                </div>
              ) : (
                <div className="col-1 unset-padding btn-group-container">
                  <Button
                    onClick={() => removeNewNumberOfRunsAndPreferredLane(index)}
                    className="btn-minus"
                    data-testid={`btn-minus-${index}`}
                  >
                    -
                  </Button>
                </div>
              )}
            </ItemFormGroup>
          ))}
          <ItemWrapper>
            <label>Comments - Input VINs here (Optional)</label>
            <TextArea
              data-testid="comments"
              maxLength={1000}
              onChange={e => setComment(e.target.value)}
              rows={4}
              value={comment}
            ></TextArea>
            <p data-testid="characters-remaining" className="mt-1">
              {1000 - comment.length} character(s) remaining
            </p>
          </ItemWrapper>
          <ItemWrapper>
            <label>Contact Name</label>
            <ContactNameInput type="text" className="form-control" />
          </ItemWrapper>
          <ItemWrapper className="d-flex">
            <div className="col-7 unset-padding">
              <label>Email</label>
              <ContactNameInput type="text" className="form-control" />
            </div>
            <div className="col-5 flex-end">
              <div className="prism-radio">
                <label className="align-items-center d-flex mb-radio-mg">
                  <input id="preferredEmailOption" defaultChecked name="preferredContact" type="radio" />
                  <span className="itemCol">Preferred Contact</span>
                </label>
              </div>
            </div>
          </ItemWrapper>
          <ItemWrapper className="d-flex">
            <div className="col-7 unset-padding">
              <label>Phone (Optional)</label>
              <ContactNameInput type="text" className="form-control" />
            </div>
            <div className="col-5 flex-end">
              <div className="prism-radio">
                <label className="align-items-center d-flex mb-radio-mg">
                  <input id="preferredPhonelOption" defaultChecked name="preferredContact" type="radio" />
                  <span className="itemCol">Preferred Contact</span>
                </label>
              </div>
            </div>
          </ItemWrapper>
        </SlideOutBody>
        <SlideOutFooter>
          <button className="cancel-button btn btn-textonly">Cancel</button>
          <button id="requestRunsBtn" className="save-button btn btn-primary">
            Request
          </button>
        </SlideOutFooter>
      </SlideOut>
    </RequestRunsSlideOut>
  );
};

const RequestRunsSlideOut = styled.div`
  .slide-out-content .slide-out-close {
    color: ${({ theme }) => theme.colors.secondaryDark};
    height: 40px;
    font-size: 2.2rem;
    opacity: unset;
  }

  .slide-out-content {
    padding: unset;
  }

  .dropdown-item {
    font-size: 15px;
    font-weight: 400;
  }

  .prism-select {
    .prism-select__menu-open {
      z-index: 2003;
    }
  }

  @media only screen and (max-width: 480px) {
    .slide-out-content {
      width: 100vw;
    }

    .slide-out-content .slide-out-close {
      color: white;
      margin-top: 1rem;
    }

    .slide-out-body {
      display: flex;
      flex-direction: column;
    }
  }

  @media only screen and (min-width: 480px) and (max-width: 600px) {
    .slide-out-content {
      width: 80vw;
    }
  }

  @media only screen and (min-width: 600px) and (max-width: 801px) {
    .slide-out-content {
      width: 75vw;
    }
  }

  @media only screen and (min-width: 801px) {
    .slide-out-content {
      width: 480px;
    }
  }

  .slide-out-body {
    overflow: hidden;
  }

  .number-runs-input {
    background-image: none;
    text-transform: uppercase;
    margin: 0 0 0 0.1rem;
  }

  .btn-group-container {
    height: 4rem;
    align-self: flex-end;
    text-align: right;
  }

  .btn-plus,
  .btn-minus {
    align-items: center;
    border-radius: 4px;
    display: inline-flex;
    font-size: 1.6rem;
    justify-content: center;
    padding: 0 16px;
    text-transform: uppercase;
    border: 2px solid;
    align-self: flex-end;
    width: 36px;
  }

  .btn-plus {
    border-color: ${({ theme }) => theme.colors.secondaryDark};
    color: ${({ theme }) => theme.colors.secondaryDark};
  }

  .btn-minus {
    border-color: red;
    color: red;
  }
  .cancel-button {
    margin-right: 1.6rem;
  }

  .unset-padding {
    padding: unset;
  }

  .unset-margin {
    margin: unset;
  }

  .flex-end {
    align-self: flex-end;
  }
`;

const SlideOutHeader = styled.div`
  display: flex;
  font-weight: 700;
  text-transform: uppercase;
  line-height: 1.2;
  font-size: 2rem;
  height: 6rem;
  align-items: center;
  padding: 3rem 2.4rem 2.4rem;
  color: ${({ theme }) => theme.colors.secondaryDark};

  @media only screen and (max-width: 480px) {
    padding: 2rem 2.4rem;
    color: white;
    background-color: ${({ theme }) => theme.colors.indigo};
  }
`;

const SlideOutBody = styled.div`
  padding: 0 24px;
  overflow-y: auto;
  overflow-x: hidden;
  height: 724px;

  @media only screen and (max-width: 480px) {
    padding: 1rem 2.4rem;
    .mb-autocomplete-padding {
      padding: 0 0.5rem;
    }
    .mb-radio-mg {
      margin: auto;
    }
  }

  select {
    padding: 10px;
    padding-right: 40px;
    appearance: none;
    background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E');
    background-repeat: no-repeat, repeat;
    background-position: right 0.7em top 50%, 0 0;
    background-size: 0.65em auto, 100%;
  }

  select::-ms-expand {
    display: none;
  }
`;

const SlideOutFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 4rem 2rem;
  height: 10rem;
  width: 100%;
  background-color: white;

  @media only screen and (max-width: 480px) {
    flex-direction: column-reverse;
    background: hsla(0, 0%, 93.3%, 0.95);
    justify-content: center;

    .cancel-button {
      margin-right: inherit;
    }
  }
`;

const ItemWrapper = styled.div`
  display: block;
  width: 100%;
  margin-bottom: 16px;
`;

const TextArea = styled.textarea`
  &:focus-visible {
    outline: none;
    border: 1px solid ${({ theme }) => theme.colors.smoke};
  }
`;

const AutocompleteCustom = styled.div`
  padding: 0 1rem;
  .autoCustomInput {
    .dropdown-menu {
      z-index: 2003;
    }
  }
`;

const DropdownCustom = styled.div`
  &.prism-select__menu-open {
    overflow-y: auto;
    height: 25rem;
  }
`;

const ItemFormGroup = styled.div`
  width: 100%;
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ContactNameInput = styled.input`
  padding: 0 1rem;
  input:focus {
    border: 1px solid;
  }
`;
